import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import type { MultiSelectOption } from '@/components/common/MultiSelect';
import { parseChipString, serializeChips, type ChipKey } from './lineFilters';
import { BOARD_PATHS, useBoardKind } from '../components/boardKindContext';

type ReplenishmentSearch = Record<string, unknown>;

/**
 * Search + chips + ship-to rail selection, URL-backed so the detail modal's
 * prev/next sibling navigation honors the same filter. The modal independently
 * scopes siblings to the current line's ship-to, so the rail selection stays
 * consistent there too.
 */
export function useAssignmentUrlFilters() {
  const navigate = useNavigate();
  const boardPath = BOARD_PATHS[useBoardKind()];
  const searchParams = useSearch({ strict: false }) as {
    chips?: string;
    q?: string;
    shipTo?: string;
  };
  const search = searchParams.q ?? '';
  const selectedShipTo = searchParams.shipTo ?? null;
  const chips = useMemo(
    () => parseChipString(searchParams.chips),
    [searchParams.chips],
  );

  const patchSearch = useCallback(
    (patch: ReplenishmentSearch) => {
      // Stay on whichever board is open (buyer or transfer, GEN-3026).
      void navigate({
        to: boardPath,
        search: (prev: ReplenishmentSearch) => ({ ...prev, ...patch }),
        replace: true,
        resetScroll: false,
      });
    },
    [navigate, boardPath],
  );

  const setSearch = useCallback(
    (next: string) => patchSearch({ q: next ? next : undefined }),
    [patchSearch],
  );

  const toggleChip = useCallback(
    (key: ChipKey) => {
      const next = new Set(chips);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      patchSearch({ chips: serializeChips(next) });
    },
    [chips, patchSearch],
  );

  const selectShipTo = useCallback(
    (shipTo: string | null) => patchSearch({ shipTo: shipTo ?? undefined }),
    [patchSearch],
  );

  return { search, selectedShipTo, chips, setSearch, toggleChip, selectShipTo };
}

function idSet(options: MultiSelectOption[]): Set<string> {
  return new Set(options.map((o) => o.id));
}

/**
 * Ship-to / warehouse / hub focus pickers: local working state (not
 * URL-backed) that resets when the supplier changes.
 */
export function useFocusPickerState(supplierId: string) {
  const [shipToFilter, setShipToFilter] = useState<MultiSelectOption[]>([]);
  const [warehouseFilter, setWarehouseFilter] = useState<MultiSelectOption[]>(
    [],
  );
  const [hubFilter, setHubFilter] = useState<MultiSelectOption[]>([]);
  // Reset on supplier change during render ("adjust state when a prop
  // changes"); state rather than a ref so React Compiler can memoize.
  const [filtersFor, setFiltersFor] = useState(supplierId);
  if (filtersFor !== supplierId) {
    setFiltersFor(supplierId);
    setShipToFilter([]);
    setWarehouseFilter([]);
    setHubFilter([]);
  }

  // React Compiler memoizes these.
  const shipToIds = idSet(shipToFilter);
  const warehouseIds = idSet(warehouseFilter);
  const hubNames = idSet(hubFilter);

  return {
    shipToFilter,
    setShipToFilter,
    warehouseFilter,
    setWarehouseFilter,
    hubFilter,
    setHubFilter,
    shipToIds,
    warehouseIds,
    hubNames,
  };
}
